import { Chat, Thread } from '@/types';

import { BlueJApi } from './blue-j-api';
import { UploadFilesResponse, FileUploadValidationResponse, FileValidationDataType } from '../../attachments/types';

export class ThreadApi extends BlueJApi {
  private basePath = 'api'

  async getThread(threadId: string) {
    return this._getThread(null, { threadId }).parsedResponse();
  }

  async getThreadMeta(threadId: string) {
    return this._getThreadMeta(null, { threadId }).parsedResponse();
  }

  async generateThreadTitle(threadId: string, question: string) {
    return this._generateThreadTitle({ threadId, question }).parsedResponse();
  }

  async deleteChat(threadId: string, chatId: string) {
    return this._deleteChat({ threadId, chatId }).parsedResponse();
  }

  async getChat(threadId:string, chatId: string) {
    return this._getChat(null, { chatId, threadId }).parsedResponse();
  }

  async getChats(threadId: string, chatIds: string[]) {
    return this._getChats({ chatIds }, { threadId }).parsedResponse();
  }

  async abortChat(threadId: string, chatId: string) {
    return this._abortChat({ chatId, threadId }).parsedResponse();
  }

  async validateFileUpload(files: File[], provider: 'openai' | 'google', targetId: string, targetType: 'thread') {
    const parsedFiles = files.map((file: File) => ({
      name: file.name,
      mimeType: file.type,
      size: file.size
    }));
    return this._validateFileUploads({ files: parsedFiles }, { provider, targetId, targetType }).parsedResponse();
  }

  async uploadFiles(files: FormData, provider: 'openai' | 'google', targetId: string, targetType: 'thread') {
    return this._uploadFiles(files, { provider, targetId, targetType }).parsedResponse();
  }

  async deleteAttachments(targetId: string, targetType: 'thread', attachmentIds: string[]) {
    return this._deleteAttachments({ targetId, targetType, attachmentIds }).parsedResponse();
  }

  async pinThread(threadId: string) {
    return this._pinThread({ threadId }).parsedResponse();
  }

  async unpinThread(threadId: string) {
    return this._unpinThread({ threadId }).parsedResponse();
  }

  async sortPinnedThreads(threadIds: string[]) {
    return this._sortPinnedThreads({ threadIds }).parsedResponse();
  }

  private _getThread = this.createFetch<null, Thread, { threadId: string }>(params => `${this.basePath}/thread/${params.threadId}`, 'GET');
  private _getThreadMeta = this.createFetch<null, Thread, { threadId: string }>(params => `${this.basePath}/thread-meta/${params.threadId}`, 'GET');
  private _getChat = this.createFetch<null, Chat, { threadId: string, chatId: string }>(params => `threads/${params.threadId}/chats/${params.chatId}?includePending=true`, 'GET');
  private _deleteChat = this.createFetch<{ threadId: string, chatId: string }, Chat, void>(`${this.basePath}/delete-chat`, 'POST');
  private _getChats = this.createFetch<{ chatIds: string[] }, Chat[], { threadId: string }>(params => `threads/${params.threadId}/chats`, 'POST');
  private _abortChat = this.createFetch<{ chatId: string, threadId: string }, Chat, void>(`${this.basePath}/abort-chat`, 'POST');
  private _generateThreadTitle = this.createFetch<{ threadId: string, question: string }, Thread, void>(`${this.basePath}/generate-thread-title`, 'POST');
  private _validateFileUploads = this.createFetch<{ files: FileValidationDataType[] }, FileUploadValidationResponse, { provider: 'openai' | 'google', targetType: 'thread', targetId: string }>((params) => `${this.basePath}/validate-file-uploads?provider=${params?.provider}&targetType=${params?.targetType}&targetId=${params?.targetId}`, 'POST');
  private _uploadFiles = this.createFormDataFetch<UploadFilesResponse, { provider: 'openai' | 'google', targetType: 'thread', targetId: string }>((params) => `${this.basePath}/upload-files?provider=${params?.provider}&targetType=${params?.targetType}&targetId=${params?.targetId}`);
  private _deleteAttachments = this.createFetch<{ targetId: string, targetType: 'thread', attachmentIds: string[] }, { deleted: string[] }, void>(`${this.basePath}/delete-attachments`, 'POST');
  private _pinThread = this.createFetch<{ threadId: string }, Thread, void>(`${this.basePath}/pin-thread`, 'POST');
  private _unpinThread = this.createFetch<{ threadId: string }, Thread, void>(`${this.basePath}/unpin-thread`, 'POST');
  private _sortPinnedThreads = this.createFetch<{ threadIds: string[] }, void, void>(`${this.basePath}/sort-pinned-threads`, 'POST');
}
